import { useMemo, useState } from "react";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import dayjs from "dayjs";
import { processExam } from "../../../components/Api/ExamGradingEndpoints";
import { Button } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useAtomValue } from "jotai";
import {
	examProcessingStateAtom,
	allStudentsMatchedAtom,
	examInDatabaseAtom,
	outputSettingsAtom,
	currentOmrRequestAtom,
	classListFileAtom,
} from "../../../components/Atoms/atoms";
import {
	buildKeyMapByVersion,
	computeExamMaxScore,
	getBonusMarksFromOutputSettings,
	getUniqueWeightBreakdownValues,
	gradeResponseString,
	isWeightedExam,
	normalizeSelectedQuestions,
} from "../utils/scoring";
import { getSectionGroupsFromStudents, parseRootClassIds } from "../utils/classSections";
import {
	compareVersionCodesForDisplay,
	normalizeVersionCode,
	normalizeVersionList,
	resolveDisplayVersionCode,
	resolveStudentVersion,
} from "../utils/versionResolution";
import {
	getAnalysisKeyModificationSummary,
	getKeyModificationSummaryLines,
	renderKeyModificationSummary,
} from "../utils/keyModificationSummary";

function getKeyForVersion(answerKeys, versionCode) {
	const keys = Array.isArray(answerKeys) ? answerKeys : [];
	const target = normalizeVersionCode(
		versionCode,
		normalizeVersionCode(keys[0]?.version, "single"),
	);
	return keys.find((k) => normalizeVersionCode(k?.version, "single") === target) || keys[0] || null;
}

function resolveStudentFormCode(student, knownVersions = []) {
	return resolveStudentVersion(student, knownVersions, "single");
}

const getBestScore = (
	student,
	outputSettings,
	answerKeys,
	header,
	keyMapByVersion,
	versionOverride = null,
) => {
	const { score } = getStudentScoreDetails(
		student,
		outputSettings,
		answerKeys,
		header,
		keyMapByVersion,
		versionOverride,
	);
	return Number(score ?? 0);
};

function formatPdfScoreValue(rawValue) {
	const score = Number(rawValue);
	if (!Number.isFinite(score)) return "0";
	return Number(score.toFixed(3)).toString();
}

function buildWeightBreakdownColumns(weightBreakdown = {}, orderedWeightValues = []) {
	if (!Array.isArray(orderedWeightValues) || orderedWeightValues.length === 0) return [];
	return orderedWeightValues.map((weightValue) =>
		formatPdfScoreValue(weightBreakdown?.[weightValue] || 0),
	);
}

function getStudentScoreDetails(
	student,
	outputSettings,
	answerKeys,
	header,
	keyMapByVersion,
	versionOverride = null,
) {
	const keyObj = getKeyForVersion(answerKeys, versionOverride ?? student?.version);
	if (!keyObj || !header) return { score: 0, weightBreakdown: {} };
	const keyVersion = normalizeVersionCode(keyObj?.version, "single");
	const answerSetByQ = keyMapByVersion[keyVersion] || keyMapByVersion.single || {};
	const resp = student?.std_response ?? student?.response ?? student?.std_response_str ?? "";
	const { score, weightBreakdown } = gradeResponseString(
		resp,
		answerSetByQ,
		keyObj,
		header,
		outputSettings,
	);
	return {
		score: Number(score ?? 0),
		weightBreakdown: weightBreakdown || {},
	};
}

function appendSuffixToFilename(filename, filenameSuffix = "") {
	if (!filenameSuffix || !filename) return filename;
	const dotIndex = filename.lastIndexOf(".");
	if (dotIndex <= 0) return `${filename}${filenameSuffix}`;
	return `${filename.slice(0, dotIndex)}${filenameSuffix}${filename.slice(dotIndex)}`;
}

function renderGroupedKeyModificationSummary(doc, groups = [], options = {}) {
	const marginLeft = options?.marginLeft ?? 40;
	const marginRight = options?.marginRight ?? 40;
	const lineHeight = options?.lineHeight ?? 12;
	const headingGap = options?.headingGap ?? 14;
	const sectionGap = options?.sectionGap ?? 8;
	const maxWidth = Number(doc?.internal?.pageSize?.getWidth?.() || 612) - marginLeft - marginRight;

	let y = options?.startY ?? 0;
	let rendered = false;

	for (const group of groups || []) {
		const lines = getKeyModificationSummaryLines(group?.summary);
		if (lines.length === 0) continue;

		rendered = true;
		doc.setFont(undefined, "bold");
		doc.text(String(group?.label || ""), marginLeft, y);
		y += headingGap;
		doc.setFont(undefined, "normal");

		for (const line of lines) {
			const wrapped = doc.splitTextToSize(line, maxWidth);
			for (const chunk of wrapped) {
				doc.text(chunk, marginLeft, y);
				y += lineHeight;
			}
		}

		y += sectionGap;
	}

	doc.setFont(undefined, "normal");
	return rendered ? y : (options?.startY ?? 0);
}

export default function SrtExmPDF({ token, reqnum, downloadingAll, filenameSuffix = "" }) {
	const allStudentsMatched = useAtomValue(allStudentsMatchedAtom);
	const examProcessingState = useAtomValue(examProcessingStateAtom);
	const examInDatabase = useAtomValue(examInDatabaseAtom);
	const outputSettings = useAtomValue(outputSettingsAtom);
	const classListFile = useAtomValue(classListFileAtom);
	const currentOmrRequest = useAtomValue(currentOmrRequestAtom);
	const selectedQuestions = normalizeSelectedQuestions(
		outputSettings?.selectedQuestions,
		examProcessingState?.header?.lqnum,
		examProcessingState?.header?.hqnum,
	);
	const noQuestionsSelected =
		Array.isArray(outputSettings?.selectedQuestions) && selectedQuestions?.size === 0;
	const rootClassIds = useMemo(
		() => parseRootClassIds(currentOmrRequest?.class_ids),
		[currentOmrRequest?.class_ids],
	);
	const sectionGroups = useMemo(
		() =>
			getSectionGroupsFromStudents({
				students: examProcessingState?.students || [],
				classListRows: classListFile?.classlist || [],
				rootClassIds,
			}),
		[examProcessingState?.students, classListFile?.classlist, rootClassIds],
	);
	const shouldSplitBySection = Boolean(
		outputSettings?.downloadPerClassSection && rootClassIds.length > 1 && sectionGroups.length > 0,
	);

	const [loadingSrtExm, setLoadingSrtExm] = useState(false);
	const processSrtExm = async () => {
		setLoadingSrtExm(true);
		try {
			if (shouldSplitBySection) {
				for (const { classId, students } of sectionGroups) {
					await runSrtExmPDF({
						examProcessingState,
						token,
						outputSettings,
						filenameSuffix,
						sectionClassId: classId,
						sectionStudentRspIds: students.map((student) => student.rsp_id),
					});
				}
				return;
			}

			await runSrtExmPDF({ examProcessingState, token, outputSettings, filenameSuffix });
		} finally {
			setLoadingSrtExm(false);
		}
	};

	return (
		<Button
			onClick={processSrtExm}
			variant={"outlined"}
			color="primary"
			sx={{ borderRadius: "30px" }}
			disabled={
				!examProcessingState ||
				examProcessingState.length === 0 ||
				reqnum !== examProcessingState.header.reqnum ||
				!examInDatabase ||
				!allStudentsMatched ||
				noQuestionsSelected ||
				downloadingAll ||
				loadingSrtExm
			}
		>
			{downloadingAll || loadingSrtExm ? (
				<CircularProgress size={24} color="inherit" />
			) : (
				"Download"
			)}
		</Button>
	);
}
export async function runSrtExmPDF({
	examProcessingState,
	token,
	outputSettings,
	filenameSuffix = "",
	sectionClassId = null,
	sectionStudentRspIds = null,
}) {
	const { blob, filename } = await buildSrtExmPDFBlob({
		examProcessingState,
		token,
		outputSettings,
		sectionClassId,
		sectionStudentRspIds,
	});
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = appendSuffixToFilename(filename, filenameSuffix);
	a.click();
	URL.revokeObjectURL(url);
}

export async function buildSrtExmPDFBlob({
	examProcessingState,
	token,
	outputSettings,
	sectionClassId = null,
	sectionStudentRspIds = null,
}) {
	const requestedPointBiserialMode = String(outputSettings?.tabulationPointBiserialMode || "")
		.trim()
		.toLowerCase();
	const legacyFallbackPointBiserialMode =
		outputSettings?.tabulationUseCorrectedPointBiserial === false ? "rpb" : "crpb";
	const pointBiserialMode = ["crpb", "rpb", "legacy_crpb"].includes(requestedPointBiserialMode)
		? requestedPointBiserialMode
		: legacyFallbackPointBiserialMode;
	const pointBiserialHeader =
		pointBiserialMode === "legacy_crpb"
			? "POINT BISERIAL (LEGACY CRPB)"
			: pointBiserialMode === "rpb"
				? "POINT BISERIAL (RPB)"
				: "POINT BISERIAL (CRPB)";
	// Legacy compatibility: exclude discarded blank sheets from analysis denominators.
	const includeDiscardedBlankResponses = pointBiserialMode !== "legacy_crpb";

	const selectedQuestions = normalizeSelectedQuestions(
		outputSettings?.selectedQuestions,
		examProcessingState?.header?.lqnum,
		examProcessingState?.header?.hqnum,
	);
	const selectedQuestionsPayload = selectedQuestions
		? [...selectedQuestions].sort((a, b) => a - b)
		: null;
	const sectionRspSet = Array.isArray(sectionStudentRspIds)
		? new Set(sectionStudentRspIds.map((value) => String(value)))
		: null;
	const scopedStudents = sectionRspSet
		? (examProcessingState?.students || []).filter((student) =>
				sectionRspSet.has(String(student?.rsp_id)),
			)
		: examProcessingState?.students || [];

	const knownVersions = normalizeVersionList(
		(examProcessingState?.answer_keys || []).map((key) => key?.version),
	);
	const displayVersions = knownVersions;
	const getDisplayVersion = (value, fallback = "") =>
		resolveDisplayVersionCode(value, displayVersions, fallback);
	const scopedStudentsForAnalysis = scopedStudents.map((student) => {
		const formCodeInfo = resolveStudentFormCode(student, knownVersions);
		const resolvedVersion = normalizeVersionCode(formCodeInfo?.resolvedVersion, "");
		const currentVersion = normalizeVersionCode(student?.version, "");
		if (!resolvedVersion || resolvedVersion === currentVersion) return student;
		return { ...student, version: resolvedVersion };
	});

	const response = await processExam(
		token,
		examProcessingState.header.reqnum,
		includeDiscardedBlankResponses,
		{
			header: examProcessingState.header,
			answer_keys: examProcessingState.answer_keys,
			student_responses: scopedStudentsForAnalysis,
			selected_questions: selectedQuestionsPayload,
		},
	);

	const analyses = (response.analyses_by_version || [])
		.map((entry) => {
			const displayVersion = getDisplayVersion(
				entry?.version,
				normalizeVersionCode(entry?.version, ""),
			);
			const mappedAnalysis =
				entry?.analysis && Array.isArray(entry.analysis.answer_keys)
					? {
							...entry.analysis,
							answer_keys: entry.analysis.answer_keys.map((key) => ({
								...key,
								version: getDisplayVersion(
									key?.version,
									displayVersion || normalizeVersionCode(key?.version, ""),
								),
							})),
						}
					: entry?.analysis;
			return {
				...entry,
				version: displayVersion,
				analysis: mappedAnalysis,
			};
		})
		.sort((a, b) => compareVersionCodesForDisplay(a?.version, b?.version));

	const numberOfKeys = examProcessingState.answer_keys.length;

	const header = response.header;

	const keyMapByVersion = buildKeyMapByVersion(examProcessingState?.answer_keys || []);
	const answerKeys = response.answer_keys;
	const isMultiKeyExam = numberOfKeys > 1;
	const singleKeyModificationSummary = !isMultiKeyExam
		? getAnalysisKeyModificationSummary(analyses?.[0]?.analysis)
		: null;
	const isCompositeWeightedExam = Number(header?.e || 0) > 0 || isWeightedExam(answerKeys);
	const uniformWeightInfo = getUniformWeightInfoForPdf(
		examProcessingState?.answer_keys || [],
		selectedQuestions,
		header?.lqnum,
		header?.hqnum,
	);
	const shouldShowUnweightedSummary =
		isCompositeWeightedExam && Boolean(uniformWeightInfo?.isUniform);
	const numberOfQuestions = selectedQuestions
		? selectedQuestions.size
		: Number(header.hqnum) - Number(header.lqnum) + 1;
	const maxScoreForScaling = computeExamMaxScore(
		examProcessingState?.answer_keys || [],
		null,
		selectedQuestions,
	);
	const scaleFactor = getScoreScaleFactor(outputSettings, maxScoreForScaling);
	const scalingTarget = Number(outputSettings?.scalingTarget);
	const scalingNote =
		Boolean(outputSettings?.scalingEnabled) &&
		maxScoreForScaling > 0 &&
		Number.isFinite(scalingTarget) &&
		scalingTarget > 0
			? `SCORES SCALED FROM ${formatPdfNumber(maxScoreForScaling)} TO ${formatPdfNumber(
					scalingTarget,
				)}`
			: "";
	const bonusMarks = getBonusMarksFromOutputSettings(outputSettings);

	const doc = new jsPDF({ unit: "pt", format: "letter" });
	doc.setFont("courier", "normal");
	const date = dayjs().format("MMDDYY");
	const dateFormatted = dayjs().format("MM/DD/YYYY");
	const time = dayjs().format("HH:mm:ss");

	const valid = (analyses || []).filter((a) => a && a.analysis);
	const pooled = pooledMeanVar(valid);
	const score_dist_table = combineScoreDistTables(valid, pooled);
	const analysisCombinedStats = {
		student_count: pooled.Ntot,
		pooled,
		score_dist_table,
		mean_total: Number(pooled.mean.toFixed(2)),
		var_total: Number(pooled.variance.toFixed(2)),
		sd_total: Number(pooled.sd.toFixed(3)),
	};
	const analysisScaledCombinedStats = applyBonusToCombinedStats(
		scaleCombinedStats(analysisCombinedStats, scaleFactor),
		bonusMarks,
	);
	const analysisScaledStatsByVersion = analyses.map((a) => ({
		...a,
		analysis: {
			...a.analysis,
			mean_total: Number(
				(Number(a.analysis?.mean_total || 0) * scaleFactor + bonusMarks).toFixed(2),
			),
			var_total: Number(
				(Number(a.analysis?.var_total || 0) * scaleFactor * scaleFactor).toFixed(2),
			),
			sd_total: Number((Number(a.analysis?.sd_total || 0) * scaleFactor).toFixed(3)),
		},
	}));
	const weightedTabulationStats = buildTabulationStatsFromStudents(scopedStudents, {
		outputSettings,
		answerKeys: examProcessingState?.answer_keys || [],
		header,
		keyMapByVersion,
	});
	const unweightedAnswerKeys = shouldShowUnweightedSummary
		? normalizeAnswerKeysWithWeight(examProcessingState?.answer_keys || [], 1)
		: null;
	const unweightedTabulationStats = shouldShowUnweightedSummary
		? buildTabulationStatsFromStudents(scopedStudents, {
				outputSettings,
				answerKeys: unweightedAnswerKeys,
				header,
				keyMapByVersion,
			})
		: null;
	const tabulationCombinedStats = isCompositeWeightedExam
		? weightedTabulationStats.combined
		: analysisScaledCombinedStats;
	const tabulationStatsByVersion = isCompositeWeightedExam
		? weightedTabulationStats.byVersion
		: analysisScaledStatsByVersion;
	const tabulationUnweightedCombinedStats = shouldShowUnweightedSummary
		? unweightedTabulationStats?.combined
		: null;

	const choices = [
		"A",
		"B",
		"C",
		"D",
		"E",
		"F",
		"G",
		"H",
		"I",
		"J",
		"K",
		"L",
		"M",
		"N",
		"O",
		"P",
		"Q",
		"R",
		"S",
		"T",
		"U",
		"V",
		"W",
		"X",
		"Y",
		"Z",
	];
	const number_of_answer_option = header.qlen;
	const letter_choices = choices.slice(0, number_of_answer_option);
	// — Header block —
	doc.setFontSize(9);

	doc.text(`Report generated at ${time} on ${dateFormatted}`, 40, 60);
	let headerYOffset = 0;
	if (scalingNote) {
		doc.setFont(undefined, "bold");
		doc.text(scalingNote, 40, 74 + headerYOffset);
		headerYOffset += 14;
		doc.setFont(undefined, "normal");
	}
	if (bonusMarks > 0) {
		doc.setFont(undefined, "bold");
		doc.text(`BONUS MARKS APPLIED: +${bonusMarks}`, 40, 74 + headerYOffset);
		headerYOffset += 14;
		doc.setFont(undefined, "normal");
	}

	if (sectionClassId) {
		doc.setFont(undefined, "bold");
		doc.text(`SECTION: ${sectionClassId}`, 40, 74 + headerYOffset);
		headerYOffset += 14;
		doc.setFont(undefined, "normal");
	}

	//add analysis.discarded_blank_count

	if (isMultiKeyExam) {
		doc.text(`NUMBER OF ITEMS: ${numberOfQuestions}`, 40, 80 + headerYOffset);
		doc.text(`NUMBER OF DISTRACTORS: ${Number(number_of_answer_option)}`, 40, 90 + headerYOffset);
		doc.text(
			`The special code field will be used to divide this job into multiple forms.
An item analysis will be performed for each form since information is not
available to map one form into another.`,
			40,
			120 + headerYOffset,
		);

		const groupedModificationSummaryEndY = renderGroupedKeyModificationSummary(
			doc,
			analyses.map(({ version, analysis }) => ({
				label: `Version ${getDisplayVersion(
					version,
					analysis?.answer_keys?.[0]?.version ?? normalizeVersionCode(version, ""),
				)}`,
				summary: getAnalysisKeyModificationSummary(analysis),
			})),
			{
				startY: 160 + headerYOffset,
				marginLeft: 40,
				marginRight: 40,
				lineHeight: 12,
				headingGap: 14,
				sectionGap: 8,
			},
		);
		const validVersions = (analyses || []).map((a) => String(a.version));
		renderFormCodeWarnings(doc, scopedStudents, validVersions, {
			marginLeft: 40,
			startY: Math.max(160 + headerYOffset, groupedModificationSummaryEndY),
			outputSettings,
			answerKeys: examProcessingState?.answer_keys || [],
			header,
			keyMapByVersion,
		});
	} else {
		// keep a fixed two-column layout so long "(... Unweighted)" values do not overlap labels
		const textWidthNumberCases = doc.getTextWidth(`Number of Cases=`);
		const labelRightX = 40 + textWidthNumberCases;
		const valueX = labelRightX + 10;
		doc.text(`Number of Cases=`, labelRightX, 80 + headerYOffset, {
			align: "right",
		});
		doc.text(`${tabulationCombinedStats.student_count}`, valueX, 80 + headerYOffset, {
			align: "left",
		});
		doc.text(`Mean=`, labelRightX, 90 + headerYOffset, {
			align: "right",
		});
		doc.text(
			formatWeightedWithOptionalUnweighted(
				tabulationCombinedStats.mean_total,
				tabulationUnweightedCombinedStats?.mean_total,
				1,
				shouldShowUnweightedSummary,
			),
			valueX,
			90 + headerYOffset,
			{
				align: "left",
			},
		);
		doc.text(`Std. Dev.=`, labelRightX, 100 + headerYOffset, {
			align: "right",
		});
		doc.text(
			formatWeightedWithOptionalUnweighted(
				tabulationCombinedStats.sd_total,
				tabulationUnweightedCombinedStats?.sd_total,
				1,
				shouldShowUnweightedSummary,
			),
			valueX,
			100 + headerYOffset,
			{
				align: "left",
			},
		);

		renderKeyModificationSummary(doc, singleKeyModificationSummary, {
			startY: 120 + headerYOffset,
			marginLeft: 40,
			marginRight: 40,
			lineHeight: 12,
			gapAfter: 6,
		});
	}

	doc.addPage();
	if (isMultiKeyExam) {
		doc.text("FREQUENCY DISTRIBUTION OF RAW SCORES -- ALL FORMS", 40, 70);
		const distTable = tabulationCombinedStats.score_dist_table;
		// — Score distribution with Z-NORML, %-ILE & CUM-% —
		const marginLeft = 40;
		autoTable(doc, {
			head: [["SCORE", "FREQ", "PERCENT", "CUM-%"]],
			body: distTable.map((r) => [r.score, r.freq, r.pct.toFixed(2), r.cum_pct.toFixed(2)]),
			startY: 80,
			margin: { left: marginLeft },
			theme: "plain",
			headStyles: {
				cellPadding: { top: 1, right: 1, bottom: 8, left: 1 },
				halign: "center",
			},
			bodyStyles: {
				fontSize: 9,
				cellWidth: 50,
				halign: "center",
			},
			styles: {
				fontSize: 9,
				cellPadding: 0,
				// fontStyle: "courier",
				font: "courier",
				fontWeight: "normal",
			},
		});
		const lastY = doc.lastAutoTable.finalY;

		const testMeanText = `TEST MEAN= ${formatWeightedWithOptionalUnweighted(
			tabulationCombinedStats.mean_total,
			tabulationUnweightedCombinedStats?.mean_total,
			2,
			shouldShowUnweightedSummary,
		)}`;
		const testVarianceText = `TEST VARIANCE= ${formatWeightedWithOptionalUnweighted(
			tabulationCombinedStats.var_total,
			tabulationUnweightedCombinedStats?.var_total,
			2,
			shouldShowUnweightedSummary,
		)}`;
		const testSDText = `TEST STANDARD DEVIATION= ${formatWeightedWithOptionalUnweighted(
			tabulationCombinedStats.sd_total,
			tabulationUnweightedCombinedStats?.sd_total,
			3,
			shouldShowUnweightedSummary,
		)}`;
		doc.text(testMeanText, 40, lastY + 20);
		doc.text(testVarianceText, 40, lastY + 30);
		doc.text(testSDText, 40, lastY + 40);
		doc.addPage();

		analyses.forEach(({ version, analysis }, vi) => {
			if (vi > 0) {
				doc.addPage();
			}

			if (numberOfKeys > 1) {
				const versionLabel = getDisplayVersion(
					version,
					analysis?.answer_keys?.[0]?.version ?? normalizeVersionCode(version, ""),
				);
				const textWidthVersion = doc.getTextWidth(`Form Number ${versionLabel}`);
				doc.setFont(undefined, "bold");
				doc.text(
					`Form Number ${versionLabel}`,
					(doc.internal.pageSize.getWidth() - textWidthVersion) / 2,
					40,
				);
				doc.setFont(undefined, "normal");
			}

			const pointBiserialTableLayout = getPointBiserialTableLayout(doc, number_of_answer_option);
			const gapInfo = detectDisplayGapFromUsedKey(
				analysis?.used_key?.used_key_string?.split("|") || [],
				header?.lqnum,
			);
			const excludedGapQs = buildExcludedGapSet(gapInfo);
			const excludedRangeMessage = formatExcludedRangeMessage(gapInfo);
			let tableStartY = 60;
			if (excludedRangeMessage) {
				const lines = doc.splitTextToSize(
					excludedRangeMessage,
					doc.internal.pageSize.getWidth() -
						pointBiserialTableLayout.marginLeft -
						pointBiserialTableLayout.marginRight,
				);
				lines.forEach((line, index) => {
					doc.text(line, pointBiserialTableLayout.marginLeft, 60 + index * 12);
				});
				tableStartY += lines.length * 12 + 8;
			}

			const items = analysis.items
				.filter((item) => {
					const itemNumber = Number(header?.lqnum) + Number(item?.index) - 1;
					if (!excludedGapQs) return true;
					return !excludedGapQs.has(itemNumber);
				})
				.map((item) => {
					const cnts = analysis.group_breakdown["total"].counts[item.index];
					const props = analysis.group_breakdown["total"].proportions[item.index];
					const totalAnsweredCorrect = cnts[item.correct_options[0]] || 0;
					const rpb = Number(item?.rpb);
					const crpb = Number(item?.crpb);
					const legacyCrpbFromApi = Number(item?.legacy_crpb);
					const p = Number(item?.dif);
					const sdTotal = Number(analysis?.sd_total);
					const legacyCrpbRaw = Number.isFinite(legacyCrpbFromApi)
						? legacyCrpbFromApi
						: Number.isFinite(rpb) &&
							  Number.isFinite(p) &&
							  Number.isFinite(sdTotal) &&
							  sdTotal > 0 &&
							  p > 0 &&
							  p < 1
							? rpb - Math.sqrt(p * (1 - p)) / sdTotal
							: rpb;
					const pointBiserialRaw =
						pointBiserialMode === "rpb"
							? rpb
							: pointBiserialMode === "legacy_crpb"
								? legacyCrpbRaw
								: crpb;
					const pointBiserial = Number.isFinite(pointBiserialRaw) ? pointBiserialRaw : 0;

					return {
						...item,
						itemNumber: Number(header?.lqnum) + Number(item?.index) - 1,
						pointBiserial,
						totalAnsweredCorrect,
						options: item.options.map((opt) => {
							const p = props[opt] != null ? (props[opt] * 100).toFixed(1) : "0";
							return {
								...opt,
								label: `${p}${item.correct_options.includes(opt) ? "*" : ""}`,
							};
						}),
					};
				});

			autoTable(doc, {
				head: [["ITEM", pointBiserialHeader, ...letter_choices, "ANSWERED CORRECT"]],
				body: items.map((i) => [
					i.itemNumber,
					i.pointBiserial.toFixed(3),
					...i.options.map((o) => o.label),
					i.totalAnsweredCorrect,
				]),
				startY: tableStartY,
				margin: {
					left: pointBiserialTableLayout.marginLeft,
					right: pointBiserialTableLayout.marginRight,
				},
				theme: "plain",
				headStyles: {
					...pointBiserialTableLayout.headStyles,
				},
				bodyStyles: {
					...pointBiserialTableLayout.bodyStyles,
				},
				styles: {
					font: "courier",
					// fontStyle: "courier",
					...pointBiserialTableLayout.styles,
				},
				columnStyles: pointBiserialTableLayout.columnStyles,
				didParseCell: function (data) {
					if (
						data.section === "body" &&
						typeof data.cell.raw === "string" &&
						data.cell.raw.includes("*")
					) {
						data.cell.styles.fontStyle = "bold";
						//if the value of the cell contains a - symbol, color text red
					}
				},
			});
		});

		doc.addPage();
		autoTable(doc, {
			head: [["FORM", "MEAN", "VARIANCE", "STANDARD DEVIATION", "SAMPLE SIZE"]],
			body: tabulationStatsByVersion.map((a) => {
				const row = a.analysis;
				//ensure all statistics are to two decimal places, including .00
				return [
					String(a.version),
					row.mean_total.toFixed(2),
					row.var_total.toFixed(2),
					row.sd_total.toFixed(2),
					row.student_count,
				];
			}),
			startY: 100,
			margin: { left: marginLeft },
			theme: "plain",
			headStyles: {
				cellPadding: { top: 1, right: 1, bottom: 8, left: 1 },
				halign: "center",
			},
			bodyStyles: {
				fontSize: 9,
				cellWidth: 60,
				halign: "center",
			},
			styles: {
				fontSize: 9,
				cellPadding: 1,
				//fontStyle: "courier",
				font: "courier",
			},
		});

		const lastY2 = doc.lastAutoTable.finalY;

		const { df1, df2, F, p } = oneWayAnovaFromAnalyses(tabulationStatsByVersion);

		// print the ANOVA block
		const y0 = (doc.lastAutoTable?.finalY || lastY2) + 20;
		doc.text("ANALYSIS OF VARIANCE BETWEEN GROUPS:", 40, y0);
		doc.text(`F RATIO IS ${Number(F).toFixed(3)}`, 40, y0 + 14);
		doc.text(
			`PROBABILITY OF ${Number(F).toFixed(3)} ( ${Number(df1).toFixed(
				0,
			)}, ${Number(df2).toFixed(0)} )= ${Number(p).toFixed(4)}`,
			40,
			y0 + 28,
		);
		doc.addPage();
	}

	const numKeys = examProcessingState.answer_keys.length;
	renderAlphabeticalStudentList(doc, scopedStudents, {
		marginLeft: 40,
		startY: 60,
		outputSettings,
		answerKeys: examProcessingState?.answer_keys || [],
		header,
		keyMapByVersion,
		numKeys,
	});
	//list students out
	const pageCount = doc.getNumberOfPages();
	for (let i = 1; i <= pageCount; i++) {
		doc.setPage(i);
		// set X to width - length of text to place it at the right edge INSTEAD OF 520

		const text = `Page ${i} of ${pageCount}`;
		const textWidth = doc.getTextWidth(text);
		const textWidthReqnum = doc.getTextWidth(`${header.reqnum}`);
		doc.text(text, doc.internal.pageSize.getWidth() - textWidth - 40, 20);
		doc.text(`${header.reqnum}`, 40, 20);
		doc.text(`:${header.coursetitle}`, 40 + textWidthReqnum + 10, 20);
		const textWidthCTitle = doc.getTextWidth(`:${header.coursetitle}`);
		doc.text(`${date}`, 40 + textWidthReqnum + textWidthCTitle + 30, 20);
	}
	const sectionSuffix = sectionClassId ? `_${sectionClassId}` : "";
	if (isMultiKeyExam) {
		const filename = `${response.header.reqnum}_exm${sectionSuffix}.pdf`;
		const blob = doc.output("blob");
		return { blob, filename };
	} else {
		const filename = `${response.header.reqnum}_srt${sectionSuffix}.pdf`;
		const blob = doc.output("blob");
		return { blob, filename };
	}
}

function getScoreScaleFactor(outputSettings, maxScore) {
	const target = Number(outputSettings?.scalingTarget);
	const enabled = Boolean(outputSettings?.scalingEnabled);
	if (!enabled || maxScore <= 0 || !Number.isFinite(target) || target <= 0) {
		return 1;
	}
	return target / maxScore;
}

function formatPdfNumber(value, decimals = 3) {
	const num = Number(value);
	if (!Number.isFinite(num)) return "0";
	return Number.isInteger(num) ? `${num}` : `${Number(num.toFixed(decimals))}`;
}

function scaleCombinedStats(stats, factor) {
	if (!stats || factor === 1) return stats;
	return {
		...stats,
		mean_total: Number((Number(stats.mean_total || 0) * factor).toFixed(2)),
		var_total: Number((Number(stats.var_total || 0) * factor * factor).toFixed(2)),
		sd_total: Number((Number(stats.sd_total || 0) * factor).toFixed(3)),
		score_dist_table: (stats.score_dist_table || []).map((row) => ({
			...row,
			score: Number((Number(row.score || 0) * factor).toFixed(2)),
		})),
	};
}

function applyBonusToCombinedStats(stats, bonusMarks) {
	if (!stats || !Number.isFinite(bonusMarks) || bonusMarks <= 0) return stats;
	return {
		...stats,
		mean_total: Number((Number(stats.mean_total || 0) + bonusMarks).toFixed(2)),
		score_dist_table: (stats.score_dist_table || []).map((row) => ({
			...row,
			score: Number((Number(row.score || 0) + bonusMarks).toFixed(2)),
		})),
	};
}

function hasExactFormCodeMatch(formCodeInfo, numKeys = 0) {
	if (numKeys <= 1) return true;
	return Boolean(formCodeInfo?.exactRawMatch);
}

function formatEnteredFormCode(rawVersion) {
	const raw = String(rawVersion ?? "");
	if (!raw) return "(blank)";
	return raw;
}

function formatAssignedFormCode(version) {
	const normalized = String(version ?? "").trim();
	return normalized || "(blank)";
}

function renderAlphabeticalStudentList(doc, responses, opts = {}) {
	const marginLeft = opts.marginLeft ?? 20;
	const startY = opts.startY ?? 60;
	const marginRight = opts.marginRight ?? 40;
	const outputSettings = opts.outputSettings;
	const answerKeys = opts.answerKeys || [];
	const header = opts.header;
	const keyMapByVersion = opts.keyMapByVersion || {};
	const numKeys = opts.numKeys ?? 0;
	const knownVersions = normalizeVersionList((answerKeys || []).map((k) => k?.version));
	const selectedQuestions = normalizeSelectedQuestions(
		outputSettings?.selectedQuestions,
		header?.lqnum,
		header?.hqnum,
	);
	const showWeightBreakdown =
		Boolean(outputSettings?.includeWeightBreakdownColumns) &&
		isWeightedExam(answerKeys, selectedQuestions);
	const orderedWeightValues = showWeightBreakdown
		? getUniqueWeightBreakdownValues(answerKeys, selectedQuestions)
		: [];
	const rows = (responses || [])
		.map((s) => {
			const seq = s?.sheet_sequence != null ? String(s.sheet_sequence) : "";
			const name = String(
				s?.std_name_raw ? s?.std_name_raw : s?.std_name_matched ? s?.std_name_matched : "(no name)",
			)
				.trim()
				.toUpperCase();
			const id = String(s?.std_id_raw ?? "");
			const formCodeInfo = resolveStudentFormCode(s, knownVersions);
			const version = numKeys > 1 ? formCodeInfo.resolvedVersion : "";
			const { score, weightBreakdown } = getStudentScoreDetails(
				s,
				outputSettings,
				answerKeys,
				header,
				keyMapByVersion,
				numKeys > 1 ? formCodeInfo.resolvedVersion : null,
			);
			const scoreDisplay = formatPdfScoreValue(score);
			const weightBreakdownColumns = showWeightBreakdown
				? buildWeightBreakdownColumns(weightBreakdown, orderedWeightValues)
				: [];
			const rawVersion = numKeys > 1 ? formCodeInfo.rawVersion : "";
			const needsVersionReview = !hasExactFormCodeMatch(formCodeInfo, numKeys);
			return {
				seq: seq.padStart(Math.max(4, seq.length), "0"), // "0050" look
				name,
				id,
				version,
				score,
				scoreDisplay,
				weightBreakdownColumns,
				rawVersion,
				needsVersionReview,
			};
		})
		.sort((a, b) => a.name.localeCompare(b.name, "en", { sensitivity: "base", numeric: true }));
	const showFormColumn = numKeys > 1;
	const showAssignedFormColumn = rows.some((row) => row.needsVersionReview);
	const baseColumnWidths = showFormColumn ? [50, 150, 50, 60] : [50, 150, 50];
	const scoreColumnWidth = showWeightBreakdown ? 54 : 70;
	const weightColumnWidth = 42;
	const assignedFormColumnWidth = 60;
	const columnStyles = {
		0: { cellWidth: baseColumnWidths[0], halign: "right" },
		1: { cellWidth: baseColumnWidths[1], halign: "left" },
		2: { cellWidth: baseColumnWidths[2], halign: "left" },
		[showFormColumn ? 3 : 3]: { cellWidth: scoreColumnWidth, halign: "right" },
	};

	if (showFormColumn) {
		columnStyles[3] = { cellWidth: baseColumnWidths[3], halign: "left" };
		columnStyles[4] = { cellWidth: scoreColumnWidth, halign: "right" };
	}

	orderedWeightValues.forEach((_, index) => {
		columnStyles[(showFormColumn ? 5 : 4) + index] = {
			cellWidth: weightColumnWidth,
			halign: "right",
		};
	});

	if (showAssignedFormColumn) {
		columnStyles[(showFormColumn ? 5 : 4) + orderedWeightValues.length] = {
			cellWidth: assignedFormColumnWidth,
			halign: "left",
		};
	}

	const headRow = ["SEQ", "NAME", "ID"];
	if (showFormColumn) {
		headRow.push("FORM");
	}
	headRow.push("SCORE", ...orderedWeightValues.map(String));

	if (showAssignedFormColumn) {
		headRow.push("ASSIGNED");
	}

	autoTable(doc, {
		head: [headRow],
		showHead: "firstPage",
		body: rows.map((r) => {
			const row = [r.seq, r.name, r.id];

			if (showFormColumn) {
				row.push(r.needsVersionReview ? formatEnteredFormCode(r.rawVersion) : r.version);
			}

			row.push(r.scoreDisplay, ...r.weightBreakdownColumns);

			if (showAssignedFormColumn) {
				row.push(r.needsVersionReview ? `form ${formatAssignedFormCode(r.version)}` : "");
			}

			return row;
		}),
		startY,
		margin: { top: 60, left: marginLeft, right: marginRight, bottom: 40 }, // <-- controls next pages
		theme: "plain",
		headStyles: {
			fontSize: 9,
			font: "courier",
			fontStyle: "bold",
			cellPadding: { top: 1, right: 6, bottom: 4, left: 4 },
			fillColor: false,
			textColor: 20,
		},

		styles: {
			fontSize: 9,
			//fontStyle: "courier",
			font: "courier",
			fontWeight: "normal",
			cellPadding: { top: 1, right: 6, bottom: 1, left: 4 },
			overflow: "linebreak",
			halign: "left",
			valign: "middle",
		},
		columnStyles,
		didParseCell: (data) => {
			if (data.section !== "head") return;
			const columnStyle = columnStyles[data.column.index];
			if (columnStyle?.halign) {
				data.cell.styles.halign = columnStyle.halign;
			}
		},
	});
}

function renderFormCodeWarnings(doc, responses, validVersions = [], opts = {}) {
	const marginLeft = opts.marginLeft ?? 40;
	const startY = opts.startY ?? 80;
	const marginRight = opts.marginRight ?? 40;
	const lineH = opts.lineHeight ?? 12;
	const pageH = doc.internal.pageSize.getHeight();
	const maxWidth = doc.internal.pageSize.getWidth() - marginLeft - marginRight;
	const outputSettings = opts.outputSettings;
	const answerKeys = opts.answerKeys || [];
	const header = opts.header;
	const keyMapByVersion = opts.keyMapByVersion || {};
	const allVersions = normalizeVersionList([
		...(validVersions || []),
		...(answerKeys || []).map((k) => k?.version),
	]);

	// Build student rows once
	const students = (responses || []).map((s) => {
		const seq = s?.sheet_sequence != null ? String(s.sheet_sequence) : "";
		const seqPadded = seq.padStart(Math.max(4, seq.length), "0");
		const name = String(
			s?.std_name_raw ? s?.std_name_raw : s?.std_name_matched ? s?.std_name_matched : "(no name)",
		)
			.trim()
			.toUpperCase();
		const formCodeInfo = resolveStudentFormCode(s, allVersions);
		const rawVersion = formCodeInfo.rawVersion;
		const assignedVersion = formCodeInfo.resolvedVersion;
		const needsVersionReview = !hasExactFormCodeMatch(formCodeInfo, allVersions.length);
		const studentScore = getBestScore(
			s,
			outputSettings,
			answerKeys,
			header,
			keyMapByVersion,
			assignedVersion,
		);
		const alternateScores = allVersions
			.filter((version) => version !== assignedVersion)
			.map((version) => ({
				version,
				score: getBestScore(s, outputSettings, answerKeys, header, keyMapByVersion, version),
			}));
		const bestAlt = alternateScores.reduce((best, b) => (b.score > best.score ? b : best), {
			version: null,
			score: -Infinity,
		});
		// BAD if the entered code does not exactly match a known version, or if version was guessed.
		// No exact form-code match if the entered code did not match a known version exactly.

		// “Higher score” if some other known key beats the student’s score
		const hasHigher = Number.isFinite(bestAlt.score) && bestAlt.score > studentScore;

		return {
			name,
			seqPadded,
			rawVersion,
			assignedVersion,
			studentScore,
			bestAlt, // { version, score }
			needsVersionReview,
			hasHigher,
		};
	});

	students.forEach((s) => {
		const rawSeq = String(s.seqPadded || "").replace(/\D/g, "");
		s.seqNum = Number(rawSeq || s.seqPadded || 0);
	});

	// now build one combined list and print:
	const combined = students
		.filter((s) => s.needsVersionReview || s.hasHigher)
		.sort((a, b) => {
			const d = (a.seqNum ?? 0) - (b.seqNum ?? 0);
			return d !== 0
				? d
				: a.name.localeCompare(b.name, "en", {
						sensitivity: "base",
						numeric: true,
					});
		});

	// Pretty print (no headers)
	let y = startY;

	doc.setFontSize(9);

	const writeLine = (text) => {
		const lines = doc.splitTextToSize(text, maxWidth);
		for (const ln of lines) {
			if (y > pageH - 40) {
				doc.addPage();
				y = 60;
			}
			doc.text(ln, marginLeft, y);
			y += lineH;
		}
	};

	combined.forEach((s) => {
		if (s.needsVersionReview) {
			writeLine(
				`*** ${s.name} (Seq #=${s.seqPadded}): Bad Form Code. Entered=${formatEnteredFormCode(s.rawVersion)}, Key=${formatAssignedFormCode(s.assignedVersion)} will be used.`,
			);
		} else if (s.hasHigher) {
			const av = formatAssignedFormCode(s.assignedVersion);
			const bv = s.bestAlt.version ?? "";
			writeLine(
				`*** ${s.name} (Seq #=${s.seqPadded}): Form Code=${av} , but ${bv} would give a higher score: ${s.studentScore} vs ${s.bestAlt.score}`,
			);
		}
	});

	return y;
}

function normalizeTabulationScore(raw) {
	const score = Number(raw);
	if (!Number.isFinite(score)) return 0;
	return Number(score.toFixed(3));
}

function summarizeScores(scores = []) {
	const cleaned = (scores || []).map((s) => normalizeTabulationScore(s));
	const n = cleaned.length;
	if (n === 0) {
		return {
			student_count: 0,
			mean_total: 0,
			var_total: 0,
			sd_total: 0,
			mean_raw: 0,
			var_raw: 0,
			sd_raw: 0,
		};
	}
	const meanRaw = cleaned.reduce((sum, value) => sum + value, 0) / n;
	const varianceRaw = cleaned.reduce((sum, value) => sum + Math.pow(value - meanRaw, 2), 0) / n;
	const sdRaw = Math.sqrt(varianceRaw);
	return {
		student_count: n,
		mean_total: Number(meanRaw.toFixed(2)),
		var_total: Number(varianceRaw.toFixed(2)),
		sd_total: Number(sdRaw.toFixed(3)),
		mean_raw: meanRaw,
		var_raw: varianceRaw,
		sd_raw: sdRaw,
	};
}

function buildScoreDistributionFromScores(scores = [], mean = 0, sd = 0) {
	const freqByScore = new Map();
	for (const raw of scores || []) {
		const score = normalizeTabulationScore(raw);
		const key = score.toFixed(3);
		const existing = freqByScore.get(key);
		if (existing) {
			existing.freq += 1;
		} else {
			freqByScore.set(key, { score, freq: 1 });
		}
	}

	const rows = Array.from(freqByScore.values()).sort((a, b) => a.score - b.score);
	const n = rows.reduce((sum, row) => sum + row.freq, 0);
	let cum = 0;
	return rows.map(({ score, freq }) => {
		cum += freq;
		return {
			score,
			freq,
			z: sd > 0 ? (score - mean) / sd : 0,
			pct: n > 0 ? (freq / n) * 100 : 0,
			cum_pct: n > 0 ? (cum / n) * 100 : 0,
		};
	});
}

function buildTabulationStatsFromStudents(responses = [], opts = {}) {
	const outputSettings = opts.outputSettings;
	const answerKeys = Array.isArray(opts.answerKeys) ? opts.answerKeys : [];
	const header = opts.header;
	const keyMapByVersion = opts.keyMapByVersion || {};
	const knownVersions = normalizeVersionList((answerKeys || []).map((key) => key?.version));
	const defaultVersion = normalizeVersionCode(knownVersions[0], "single");

	const scoresByVersion = new Map();
	for (const key of answerKeys) {
		const version = normalizeVersionCode(key?.version, defaultVersion);
		if (!scoresByVersion.has(version)) {
			scoresByVersion.set(version, []);
		}
	}

	const allScores = [];
	for (const student of responses || []) {
		const formCodeInfo = resolveStudentFormCode(student, knownVersions);
		const resolvedVersion = normalizeVersionCode(formCodeInfo.resolvedVersion, defaultVersion);
		const score = normalizeTabulationScore(
			getBestScore(student, outputSettings, answerKeys, header, keyMapByVersion, resolvedVersion),
		);
		allScores.push(score);
		if (!scoresByVersion.has(resolvedVersion)) {
			scoresByVersion.set(resolvedVersion, []);
		}
		scoresByVersion.get(resolvedVersion).push(score);
	}

	const combinedSummary = summarizeScores(allScores);
	const combined = {
		student_count: combinedSummary.student_count,
		mean_total: combinedSummary.mean_total,
		var_total: combinedSummary.var_total,
		sd_total: combinedSummary.sd_total,
		score_dist_table: buildScoreDistributionFromScores(
			allScores,
			combinedSummary.mean_raw,
			combinedSummary.sd_raw,
		),
	};

	const byVersion = Array.from(scoresByVersion.entries())
		.sort((a, b) => compareVersionCodesForDisplay(a[0], b[0]))
		.map(([version, scores]) => {
			const summary = summarizeScores(scores);
			return {
				version,
				analysis: {
					student_count: summary.student_count,
					mean_total: summary.mean_total,
					var_total: summary.var_total,
					sd_total: summary.sd_total,
				},
			};
		});

	return { combined, byVersion };
}

function getEffectiveAnswersForWeightCheck(item) {
	if (!item || String(item?.omit || "N").toUpperCase() === "Y") return [];
	const modified = Array.isArray(item?.modified_answer) ? item.modified_answer : [];
	const original = Array.isArray(item?.original_answer)
		? item.original_answer
		: item?.original_answer
			? [item.original_answer]
			: [];
	const source = modified.length > 0 ? modified : original;
	return source
		.map((answer) =>
			String(answer || "")
				.trim()
				.toUpperCase(),
		)
		.filter(Boolean);
}

function getUniformWeightInfoForPdf(
	answerKeys = [],
	selectedQuestions = null,
	lqnum = null,
	hqnum = null,
) {
	const selectedSet = selectedQuestions instanceof Set ? selectedQuestions : null;
	const lq = Number(lqnum);
	const hq = Number(hqnum);
	const weights = [];
	for (const key of answerKeys || []) {
		for (const item of key?.items || []) {
			const qNo = Number(item?.item_no);
			if (!Number.isFinite(qNo)) continue;
			if (Number.isFinite(lq) && qNo < lq) continue;
			if (Number.isFinite(hq) && qNo > hq) continue;
			if (selectedSet && !selectedSet.has(qNo)) continue;
			if (getEffectiveAnswersForWeightCheck(item).length === 0) continue;
			const rawWeight = Number.parseFloat(item?.weight);
			const weight = Number.isFinite(rawWeight) && rawWeight > 0 ? rawWeight : 1;
			weights.push(weight);
		}
	}
	if (weights.length === 0) {
		return { isUniform: false, weight: null, count: 0 };
	}
	const first = weights[0];
	const isUniform = weights.every((w) => Math.abs(w - first) < 1e-9);
	return { isUniform, weight: first, count: weights.length };
}

function normalizeAnswerKeysWithWeight(answerKeys = [], weight = 1) {
	return (answerKeys || []).map((key) => ({
		...key,
		items: (key?.items || []).map((item) => ({
			...item,
			weight,
		})),
	}));
}

function formatWeightedWithOptionalUnweighted(
	weightedValue,
	unweightedValue,
	decimals = 1,
	includeUnweighted = false,
) {
	const weightedNum = Number(weightedValue);
	const weightedText = Number.isFinite(weightedNum)
		? `${Number(weightedNum.toFixed(decimals))}`
		: "0";
	if (!includeUnweighted) return weightedText;
	const unweightedNum = Number(unweightedValue);
	if (!Number.isFinite(unweightedNum)) return weightedText;
	return `${weightedText} (${Number(unweightedNum.toFixed(decimals))} Unweighted)`;
}

function detectDisplayGapFromUsedKey(entries, lqnum = 1) {
	if (!Array.isArray(entries) || entries.length === 0) {
		return { isNonContiguous: false, excludedRanges: [] };
	}

	const startQ = Number(lqnum) || 1;
	const isBlankToken = (value) => String(value ?? "").trim() === "";
	const excludedRanges = [];
	let blankRunStart = null;

	const closeBlankRun = (endIndex) => {
		if (blankRunStart == null) return;
		const runLength = endIndex - blankRunStart + 1;
		if (runLength >= 3) {
			excludedRanges.push({
				startQ: startQ + blankRunStart,
				endQ: startQ + endIndex,
			});
		}
		blankRunStart = null;
	};

	entries.forEach((value, index) => {
		if (isBlankToken(value)) {
			if (blankRunStart == null) blankRunStart = index;
			return;
		}
		closeBlankRun(index - 1);
	});
	closeBlankRun(entries.length - 1);

	return {
		isNonContiguous: excludedRanges.length > 0,
		excludedRanges,
	};
}

function buildExcludedGapSet(gapInfo) {
	if (!gapInfo?.isNonContiguous || !Array.isArray(gapInfo?.excludedRanges)) return null;
	const excluded = new Set();
	gapInfo.excludedRanges.forEach(({ startQ, endQ }) => {
		for (let questionNumber = Number(startQ); questionNumber <= Number(endQ); questionNumber += 1) {
			excluded.add(questionNumber);
		}
	});
	return excluded;
}

function formatExcludedRangeMessage(gapInfo) {
	const ranges = gapInfo?.excludedRanges || [];
	if (ranges.length === 0) return "";
	if (ranges.length === 1) {
		const { startQ, endQ } = ranges[0];
		return startQ === endQ
			? `Item ${startQ} was excluded from scoring.`
			: `Items ${startQ}-${endQ} were excluded from scoring.`;
	}
	return `${ranges
		.map(({ startQ, endQ }) => (startQ === endQ ? `item ${startQ}` : `items ${startQ}-${endQ}`))
		.join(", ")} were excluded from scoring.`;
}

function getPointBiserialTableLayout(doc, answerOptionCount = 0) {
	const optionCount = Math.max(Number(answerOptionCount) || 0, 0);
	const useCompactLayout = optionCount > 5;
	const marginX = useCompactLayout ? 8 : 40;
	const pageWidth = Number(doc?.internal?.pageSize?.getWidth?.() || 612);
	const usableWidth = Math.max(pageWidth - marginX * 2, 200);
	const itemWidth = useCompactLayout ? 24 : 32;
	const answeredCorrectWidth = useCompactLayout ? 56 : 84;
	const pointBiserialMinWidth = useCompactLayout ? 92 : 110;
	const answerWidth =
		optionCount > 0
			? Math.max(
					useCompactLayout ? 18 : 32,
					Math.floor(
						(usableWidth - itemWidth - answeredCorrectWidth - pointBiserialMinWidth) / optionCount,
					),
				)
			: 0;
	const pointBiserialWidth = Math.max(
		pointBiserialMinWidth,
		usableWidth - itemWidth - answeredCorrectWidth - answerWidth * optionCount,
	);
	const answerStartIndex = 2;
	const answeredCorrectIndex = optionCount + 2;
	const columnStyles = {
		0: { cellWidth: itemWidth, halign: "center" },
		1: { cellWidth: pointBiserialWidth, halign: "center" },
		[answeredCorrectIndex]: { cellWidth: answeredCorrectWidth, halign: "center" },
	};

	for (let index = 0; index < optionCount; index += 1) {
		columnStyles[answerStartIndex + index] = {
			cellWidth: answerWidth,
			halign: "center",
		};
	}

	return {
		marginLeft: marginX,
		marginRight: marginX,
		headStyles: {
			cellPadding: useCompactLayout
				? { top: 1, right: 0.5, bottom: 6, left: 0.5 }
				: { top: 1, right: 1, bottom: 8, left: 1 },
			halign: "center",
			valign: "middle",
			overflow: "linebreak",
		},
		bodyStyles: {
			fontSize: useCompactLayout ? 8 : 9,
			halign: "center",
			valign: "middle",
		},
		styles: {
			fontSize: useCompactLayout ? 8 : 9,
			cellPadding: useCompactLayout ? 0.5 : 0,
			overflow: "linebreak",
		},
		columnStyles,
	};
}

// ---------- One-way ANOVA and F p-value (no deps) ----------
function oneWayAnovaFromAnalyses(analysesByVersion) {
	// Expect each entry like { analysis: { mean_total, var_total (pop), student_count } }
	const groups = (analysesByVersion || [])
		.map((a) => ({
			n: Number(a?.analysis?.student_count || 0),
			mean: Number(a?.analysis?.mean_total || 0),
			varPop: Number(a?.analysis?.var_total || 0),
		}))
		.filter((g) => g.n > 0);

	const k = groups.length;
	const N = groups.reduce((s, g) => s + g.n, 0);
	if (k < 2 || N <= k) {
		return { df1: 0, df2: 0, F: NaN, p: NaN }; // not enough data
	}

	// Grand mean (weighted)
	const grand = groups.reduce((s, g) => s + g.n * g.mean, 0) / N;

	// SS_between and SS_within (note: varPop uses denominator n, so SSW = sum n_i * varPop_i)
	const SSb = groups.reduce((s, g) => s + g.n * Math.pow(g.mean - grand, 2), 0);
	const SSw = groups.reduce((s, g) => s + g.n * g.varPop, 0);

	const df1 = k - 1;
	const df2 = N - k;

	const MSb = SSb / df1;
	const MSw = SSw / df2;

	const F = MSw > 0 ? MSb / MSw : Infinity;
	const p = isFinite(F) ? fSurvival(F, df1, df2) : 0; // right-tail

	return { df1, df2, F, p };
}

// ---------- F distribution right-tail (survival) ----------
function fSurvival(x, df1, df2) {
	if (!(x > 0) || df1 <= 0 || df2 <= 0) return NaN;
	const a = df1 / 2;
	const b = df2 / 2;
	const z = (df1 * x) / (df1 * x + df2);
	const cdf = regularizedIncompleteBeta(z, a, b);
	return 1 - cdf;
}

// ---------- Regularized incomplete beta via continued fraction ----------
function regularizedIncompleteBeta(x, a, b) {
	if (x <= 0) return 0;
	if (x >= 1) return 1;

	const bt = Math.exp(
		logGamma(a + b) - logGamma(a) - logGamma(b) + a * Math.log(x) + b * Math.log(1 - x),
	);

	// Use symmetry to improve convergence
	if (x < (a + 1) / (a + b + 2)) {
		return (bt * betacf(a, b, x)) / a;
	} else {
		return 1 - (bt * betacf(b, a, 1 - x)) / b;
	}
}

// Continued fraction (Modified Lentz’s method)
function betacf(a, b, x) {
	const MAX_IT = 200;
	const EPS = 3e-12;
	const FPMIN = 1e-300;

	let qab = a + b;
	let qap = a + 1;
	let qam = a - 1;

	let c = 1;
	let d = 1 - (qab * x) / qap;
	if (Math.abs(d) < FPMIN) d = FPMIN;
	d = 1 / d;
	let h = d;

	for (let m = 1; m <= MAX_IT; m++) {
		let m2 = 2 * m;

		// even step
		let aa = (m * (b - m) * x) / ((qam + m2) * (a + m2));
		d = 1 + aa * d;
		if (Math.abs(d) < FPMIN) d = FPMIN;
		c = 1 + aa / c;
		if (Math.abs(c) < FPMIN) c = FPMIN;
		d = 1 / d;
		h *= d * c;

		// odd step
		aa = -((a + m) * (qab + m) * x) / ((a + m2) * (qap + m2));
		d = 1 + aa * d;
		if (Math.abs(d) < FPMIN) d = FPMIN;
		c = 1 + aa / c;
		if (Math.abs(c) < FPMIN) c = FPMIN;
		d = 1 / d;
		const del = d * c;
		h *= del;

		if (Math.abs(del - 1) < EPS) break;
	}

	return h;
}

// ---------- logGamma (Lanczos w/ reflection) ----------
function logGamma(z) {
	const p = [
		676.5203681218851, -1259.1392167224028, 771.32342877765313, -176.61502916214059,
		12.507343278686905, -0.13857109526572012, 9.9843695780195716e-6, 1.5056327351493116e-7,
	];
	if (z < 0.5) {
		// reflection formula
		return Math.log(Math.PI) - Math.log(Math.sin(Math.PI * z)) - logGamma(1 - z);
	}
	z -= 1;
	let x = 0.99999999999980993;
	for (let i = 0; i < p.length; i++) x += p[i] / (z + i + 1);
	const t = z + p.length - 0.5;
	return 0.5 * Math.log(2 * Math.PI) + (z + 0.5) * Math.log(t) - t + Math.log(x);
}

function pooledMeanVar(analyses) {
	// Back-end uses population variance (divide by N), so pool accordingly.
	const Ns = analyses.map((a) => a.analysis?.student_count || 0);
	const means = analyses.map((a) => a.analysis?.mean_total || 0);
	const vars = analyses.map((a) => a.analysis?.var_total || 0);

	const Ntot = Ns.reduce((s, n) => s + n, 0);
	if (Ntot === 0) return { Ntot: 0, mean: 0, variance: 0, sd: 0 };

	const mean = Ns.reduce((s, n, i) => s + n * (means[i] || 0), 0) / Ntot;

	// E[X^2] = var + mean^2
	const EX2 =
		Ns.reduce((s, n, i) => s + n * ((vars[i] || 0) + Math.pow(means[i] || 0, 2)), 0) / Ntot;

	const variance = Math.max(0, EX2 - mean * mean);
	const sd = Math.sqrt(variance);

	return { Ntot, mean, variance, sd };
}

function combineScoreDistTables(analyses, pooled) {
	// Sum frequencies per score across versions
	const freqByScore = new Map();
	for (const a of analyses) {
		const table = a.analysis?.score_dist_table || [];
		for (const row of table) {
			const s = Number(row.score);
			const f = Number(row.freq) || 0;
			freqByScore.set(s, (freqByScore.get(s) || 0) + f);
		}
	}

	// Sorted rows by score
	const rows = Array.from(freqByScore.entries())
		.map(([score, freq]) => ({ score: Number(score), freq: Number(freq) }))
		.sort((x, y) => x.score - y.score);

	// Use the sum of frequencies as N (most robust)
	const N = rows.reduce((t, r) => t + r.freq, 0);

	let cum = 0;
	return rows.map(({ score, freq }) => {
		cum += freq;
		return {
			score,
			freq,
			// z is optional for this table; keep it if you like for downstream use
			z: pooled?.sd > 0 ? (score - pooled.mean) / pooled.sd : 0,
			pct: N > 0 ? (freq / N) * 100 : 0, // <-- share at this score
			cum_pct: N > 0 ? (cum / N) * 100 : 0, // <-- cumulative share
		};
	});
}
